import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, Copy, ExternalLink, MessageCircle, Mail, Search } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { company } from '../mock';
import { useLang } from '../i18n/LanguageContext';
import { AGGREGATORS, CARRIERS, detectCarrier, hasValidCheckDigit, looksLikeContainer, normalizeNumber } from '../lib/carriers';

// Two modes:
// 1) REACT_APP_SEARATES_PLATFORM_ID set  -> SeaRates' official embeddable Container Tracking widget
//    (docs: https://docs.searates.com/guides/integrations/tracking). The ID is public (visible in the page HTML).
// 2) not set (default, 100% free, no account) -> "FreeTracker": detects the shipping line from the container number and
//    sends the visitor to the line's own free live tracking page (plus free multi-carrier trackers).
const PLATFORM_ID = (process.env.REACT_APP_SEARATES_PLATFORM_ID || '').trim();
const WIDGET_SRC = 'https://www.searates.com/container/widget';

// languages the widget supports; our dropdown codes that differ from the widget's are mapped first
const WIDGET_LANGS = ['ar', 'cn', 'de', 'en', 'es', 'fr', 'hi', 'it', 'jp', 'kr', 'pl', 'pt', 'ru', 'th', 'tr', 'vi'];
const WIDGET_LANG_MAP = { zh: 'cn', ja: 'jp', ko: 'kr' };
const widgetLang = (lang) => {
  const l = WIDGET_LANG_MAP[lang] || lang;
  return WIDGET_LANGS.includes(l) ? l : 'en';
};

function TrackingWidget({ platformId, lang }) {
  const holder = useRef(null);

  useEffect(() => {
    const el = holder.current;
    if (!el) return undefined;
    el.innerHTML = '';
    const root = document.createElement('div');
    root.id = 'tracking_system_root';
    root.setAttribute('data-filter', JSON.stringify({
      platform: /^\d+$/.test(platformId) ? Number(platformId) : platformId,
      lang,
      height: '720px',
    }));
    el.appendChild(root);
    // a fresh <script> element each time, so the widget also starts when the visitor comes back to this page
    const script = document.createElement('script');
    script.src = WIDGET_SRC;
    script.async = true;
    el.appendChild(script);
    return () => { el.innerHTML = ''; };
  }, [platformId, lang]);

  // notranslate: the widget has its own language setting, Google Translate must not touch it
  return <div ref={holder} className="notranslate" translate="no" data-testid="tracking-widget" />;
}

const btnPrimary = 'h-11 px-5 rounded-md bg-[color:var(--brand)] text-white text-sm font-medium inline-flex items-center gap-2 hover:bg-[color:var(--brand-2)] transition-colors';
const btnOutline = 'h-11 px-5 rounded-md border border-[color:var(--brand)] text-[color:var(--brand)] text-sm font-medium inline-flex items-center gap-2 hover:bg-[color:var(--brand-soft)] transition-colors';

function FreeTracker() {
  const [input, setInput] = useState('');
  const [num, setNum] = useState('');
  const [copied, setCopied] = useState(false);

  const copyNumber = async () => {
    try { await navigator.clipboard.writeText(num); setCopied(true); } catch { setCopied(false); }
  };
  // opening a tracking page also copies the number, so the visitor can simply paste it there
  const open = (url) => { copyNumber(); window.open(url, '_blank', 'noopener,noreferrer'); };

  const submit = (e) => {
    e.preventDefault();
    const n = normalizeNumber(input);
    setCopied(false);
    setNum(n);
  };

  const isContainer = looksLikeContainer(num);
  const carrier = num ? detectCarrier(num) : null;
  const badDigit = isContainer && !hasValidCheckDigit(num);

  return (
    <div data-testid="free-tracker">
      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 mb-6">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Container number (e.g., MSKU0439216) or B/L number"
          className="flex-1 h-14 px-5 rounded-md border border-[color:var(--line)] bg-white outline-none focus:border-[color:var(--brand)] transition text-lg notranslate"
          data-testid="track-input"
        />
        <button type="submit" className="h-14 px-6 rounded-md bg-[color:var(--brand)] text-white flex items-center justify-center gap-2 hover:bg-[color:var(--brand-2)] transition-colors" data-testid="track-submit">
          <Search size={18} /> Track
        </button>
      </form>

      {num && (
        <div className="space-y-6" data-testid="track-result">
          <div className="rounded-md border border-[color:var(--line)] p-6 bg-[color:var(--brand-soft)]/25">
            <div className="text-xs uppercase tracking-widest text-[color:var(--muted)]">Number</div>
            <div className="text-2xl font-bold text-[color:var(--ink)] notranslate">{num}</div>

            {badDigit && (
              <div className="mt-3 flex items-start gap-2 text-amber-700 bg-amber-50 border border-amber-100 rounded-md p-3 text-sm">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <span>This container number's check digit does not match. Please re-check the number for typing mistakes.</span>
              </div>
            )}

            {carrier ? (
              <div className="mt-4">
                <p className="text-sm text-[color:var(--muted)] mb-3">
                  The owner code <b className="notranslate">{num.slice(0, 4)}</b> points to <b className="notranslate">{carrier.name}</b>. Open its live tracking page below
                  (your number is copied automatically - just paste it if it is not filled in).
                </p>
                <div className="flex flex-wrap gap-3">
                  <button type="button" onClick={() => open(carrier.link ? carrier.link(num) : carrier.url)} className={btnPrimary} data-testid="open-carrier">
                    <ExternalLink size={16} /> Track on <span className="notranslate">{carrier.name}</span>
                  </button>
                  <button type="button" onClick={copyNumber} className={btnOutline}><Copy size={16} /> {copied ? 'Copied' : 'Copy number'}</button>
                </div>
              </div>
            ) : (
              <p className="mt-4 text-sm text-[color:var(--muted)]">
                {isContainer
                  ? 'We could not tell which shipping line carries this container (the owner code may belong to a container leasing company). Pick your shipping line below or use a free multi-carrier tracker.'
                  : 'For a bill of lading or booking number, pick your shipping line below or use a free multi-carrier tracker.'}
              </p>
            )}
          </div>

          <div>
            <h3 className="font-semibold text-[color:var(--ink)] mb-3">Free multi-carrier trackers</h3>
            <div className="flex flex-wrap gap-3">
              {AGGREGATORS.map((a) => (
                <button key={a.name} type="button" onClick={() => open(a.url)} className={btnOutline}><ExternalLink size={16} /> <span className="notranslate">{a.name}</span></button>
              ))}
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-[color:var(--ink)] mb-3">Track on your shipping line's website</h3>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
              {CARRIERS.map((c) => (
                <button key={c.name} type="button" onClick={() => open(c.link && isContainer ? c.link(num) : c.url)}
                  className="h-11 px-4 rounded-md border border-[color:var(--line)] text-sm text-left flex items-center justify-between gap-2 hover:border-[color:var(--brand)] hover:bg-[color:var(--brand-soft)]/40 transition-colors">
                  <span className="notranslate">{c.name}</span> <ExternalLink size={14} className="text-[color:var(--muted)] shrink-0" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function TrackPage() {
  const { lang } = useLang();
  const waMsg = 'Hello! Please share the latest status of my container. Container / B/L number: ';
  const waLink = `https://wa.me/${company.phoneRaw}?text=${encodeURIComponent(waMsg)}`;

  return (
    <>
      <Header />
      <div className="page-hero" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1524661135-423995f22d0b?q=80&w=1600&auto=format&fit=crop)', paddingTop: '80px' }}>
        <div className="text-center max-w-3xl mx-auto px-6">
          <p className="uppercase text-xs tracking-[0.4em] mb-3 opacity-80">Live Tracking</p>
          <h1 className="text-4xl md:text-6xl font-light" style={{ fontFamily: 'Playfair Display, serif' }}>Track Shipment</h1>
          <div className="mx-auto w-16 h-[2px] bg-white/70 mt-5" />
        </div>
      </div>

      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-[color:var(--muted)] mb-8 max-w-2xl">
            Enter your container number to open its live tracking - location and milestones from origin to final delivery.
          </p>

          {PLATFORM_ID ? (
            <TrackingWidget platformId={PLATFORM_ID} lang={widgetLang(lang)} />
          ) : (
            <FreeTracker />
          )}

          <div className="mt-10 rounded-md border border-[color:var(--line)] p-6 flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1">
              <div className="font-semibold text-[color:var(--ink)]">Could not find your container?</div>
              <div className="text-sm text-[color:var(--muted)]">If it is your shipment, contact us and we will check it for you.</div>
            </div>
            <a href={waLink} target="_blank" rel="noreferrer" data-testid="track-whatsapp" className="h-11 px-5 rounded-md bg-[#25D366] text-white text-sm font-medium inline-flex items-center gap-2 hover:opacity-90 transition">
              <MessageCircle size={16} /> WhatsApp us
            </a>
            <a href={`mailto:${company.email}`} data-testid="track-email" className="h-11 px-5 rounded-md border border-[color:var(--brand)] text-[color:var(--brand)] text-sm font-medium inline-flex items-center gap-2 hover:bg-[color:var(--brand-soft)] transition">
              <Mail size={16} /> Email us
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
